
import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();

  zoomScale = 1;
  zoomX = 0;
  zoomY = 0;
  zoomDragging = false;
  zoomPointerId = null;
  zoomImage = null;
  zoomMedia = null;
  zoomMoved = false;
  suppressZoomClick = false;

  connectedCallback() {
    super.connectedCallback();

    const dialog = this.refs.dialog;

    dialog.addEventListener('scroll', this.handleScroll);
    dialog.addEventListener('wheel', this.handleZoomWheel, {
      passive: false,
    });
    dialog.addEventListener(
      'pointerdown',
      this.handleZoomPointerDown
    );
    dialog.addEventListener(
      'pointermove',
      this.handleZoomPointerMove
    );
    dialog.addEventListener(
      'pointerup',
      this.handleZoomPointerUp
    );
    dialog.addEventListener(
      'pointercancel',
      this.handleZoomPointerUp
    );
    dialog.addEventListener('click', this.handleZoomClick);
    dialog.addEventListener(
      'dblclick',
      this.handleZoomDoubleClick
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const dialog = this.refs.dialog;

    dialog.removeEventListener('scroll', this.handleScroll);
    dialog.removeEventListener('wheel', this.handleZoomWheel);
    dialog.removeEventListener(
      'pointerdown',
      this.handleZoomPointerDown
    );
    dialog.removeEventListener(
      'pointermove',
      this.handleZoomPointerMove
    );
    dialog.removeEventListener(
      'pointerup',
      this.handleZoomPointerUp
    );
    dialog.removeEventListener(
      'pointercancel',
      this.handleZoomPointerUp
    );
    dialog.removeEventListener('click', this.handleZoomClick);
    dialog.removeEventListener(
      'dblclick',
      this.handleZoomDoubleClick
    );

    this.resetZoom();
  }

  async open(index, event) {
    event.preventDefault();
    this.resetZoom();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      if (targetImage) {
        this.#loadHighResolutionImage(targetImage);
      }
    };

    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage ||
      !targetImage
    ) {
      return open();
    }

    const transitionName = 'gallery-item';

    sourceImage.style.setProperty(
      'view-transition-name',
      transitionName
    );

    await startViewTransition(() => {
      open();

      sourceImage.style.removeProperty(
        'view-transition-name'
      );

      targetImage.style.setProperty(
        'view-transition-name',
        transitionName
      );
    });

    targetImage.style.removeProperty(
      'view-transition-name'
    );

    this.selectThumbnail(index, {
      behavior: 'instant',
    });
  }

  #loadHighResolutionImage(mediaContainer) {
    if (
      !mediaContainer?.classList.contains(
        'product-media-container--image'
      )
    ) {
      return false;
    }

    const image = mediaContainer.querySelector(
      'img.product-media__image'
    );

    if (!(image instanceof HTMLImageElement)) {
      return false;
    }

    const highResolutionUrl =
      image.getAttribute('data_max_resolution');

    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl)
    ) {
      return false;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();

    newImage.className = image.className;
    newImage.alt = image.alt;

    newImage.setAttribute(
      'data_max_resolution',
      highResolutionUrl
    );

    newImage.onload = () => {
      if (!image.isConnected) return;

      if (this.zoomImage === image) {
        this.zoomImage = newImage;
      }

      image.replaceWith(newImage);

      this.#highResImagesLoaded.add(highResolutionUrl);

      if (this.zoomImage === newImage) {
        this.applyZoom();
      }
    };

    newImage.src = highResolutionUrl;

    return true;
  }

  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    if (this.zoomScale > 1 || this.zoomDragging) {
      return;
    }

    const mostVisibleElement =
      await getMostVisibleElement(media);

    if (!mostVisibleElement) return;

    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(
      thumbnails.querySelectorAll('button')
    ).forEach((button, i) => {
      button.setAttribute(
        'aria-selected',
        `${i === activeIndex}`
      );
    });

    this.#loadHighResolutionImage(mostVisibleElement);

    this.dispatchEvent(
      new ZoomMediaSelectedEvent(activeIndex)
    );
  }, 50);

  async close() {
    this.resetZoom();

    const { dialog, media } = this.refs;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice()
    ) {
      return this.closeDialog();
    }

    const mostVisibleElement =
      await getMostVisibleElement(media);

    if (!mostVisibleElement) {
      return this.closeDialog();
    }

    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = 'gallery-item';

    const mediaGallery = this.closest('media-gallery');

    const slideshowActive =
      mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      return this.closeDialog();
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty(
      'view-transition-name',
      transitionName
    );

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty(
        'view-transition-name'
      );

      slide.style.setProperty(
        'view-transition-name',
        transitionName
      );

      this.closeDialog();
    });

    slide.style.removeProperty(
      'view-transition-name'
    );

    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    this.resetZoom();

    const { dialog } = this.refs;

    dialog.close();

    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  async handleThumbnailClick(index) {
    this.resetZoom();

    const behavior = prefersReducedMotion()
      ? 'instant'
      : 'smooth';

    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;

    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  async selectThumbnail(
    index,
    options = { behavior: 'smooth' }
  ) {
    if (
      !this.refs.thumbnails ||
      !this.refs.thumbnails.children.length
    ) {
      return;
    }

    if (
      isNaN(index) ||
      index < 0 ||
      index >= this.refs.thumbnails.children.length
    ) {
      return;
    }

    this.resetZoom();

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(
      thumbnails.querySelectorAll('button')
    ).forEach((button, i) => {
      button.setAttribute(
        'aria-selected',
        `${i === index}`
      );
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(
      new ZoomMediaSelectedEvent(index)
    );
  }

  // ======================================
  // INTERACTIVE ARTWORK ZOOM
  // ======================================

  getZoomImageFromEvent(event) {
    if (
      event.pointerType &&
      event.pointerType !== 'mouse'
    ) {
      return null;
    }

    const target = event.target;

    if (!(target instanceof Element)) {
      return null;
    }

    const container = target.closest(
      '.product-media-container--image'
    );

    if (
      !container ||
      !this.refs.dialog.contains(container)
    ) {
      return null;
    }

    const image = container.querySelector(
      'img.product-media__image'
    );

    if (!(image instanceof HTMLImageElement)) {
      return null;
    }

    return { image, container };
  }

  getZoomImage() {
    if (this.zoomImage?.isConnected) {
      return this.zoomImage;
    }

    if (!this.zoomMedia?.isConnected) {
      return null;
    }

    this.zoomImage = this.zoomMedia.querySelector(
      'img.product-media__image'
    );

    return this.zoomImage;
  }

  applyZoom() {
    const image = this.getZoomImage();

    if (!image) return;

    image.style.transformOrigin = 'center center';

    image.style.transform =
      `translate(${this.zoomX}px, ${this.zoomY}px) ` +
      `scale(${this.zoomScale})`;

    image.style.cursor =
      this.zoomScale > 1 ? 'grab' : 'zoom-in';

    image.style.userSelect = 'none';

    if (this.zoomMedia) {
      this.zoomMedia.style.overflow = 'hidden';
    }
  }

  resetZoom() {
    for (const media of this.refs.media || []) {
      const image = media.querySelector(
        'img.product-media__image'
      );

      if (image) {
        image.style.transform = '';
        image.style.transformOrigin = '';
        image.style.cursor = '';
        image.style.userSelect = '';
      }

      media.style.overflow = '';
    }

    this.zoomScale = 1;
    this.zoomX = 0;
    this.zoomY = 0;
    this.zoomDragging = false;
    this.zoomPointerId = null;
    this.zoomImage = null;
    this.zoomMedia = null;
    this.zoomMoved = false;
    this.suppressZoomClick = false;
  }

  clampZoomPosition() {
    const image = this.getZoomImage();

    if (!image) return;

    if (this.zoomScale <= 1) {
      this.zoomX = 0;
      this.zoomY = 0;
      return;
    }

    const maxX =
      (image.clientWidth * (this.zoomScale - 1)) / 2;

    const maxY =
      (image.clientHeight * (this.zoomScale - 1)) / 2;

    this.zoomX = Math.max(
      -maxX,
      Math.min(maxX, this.zoomX)
    );

    this.zoomY = Math.max(
      -maxY,
      Math.min(maxY, this.zoomY)
    );
  }

  handleZoomWheel = (event) => {
    const result = this.getZoomImageFromEvent(event);

    if (!result) return;

    event.preventDefault();

    const { image, container } = result;

    if (
      this.zoomMedia &&
      this.zoomMedia !== container
    ) {
      this.resetZoom();
    }

    this.zoomImage = image;
    this.zoomMedia = container;

    const factor =
      event.deltaY < 0 ? 1.2 : 1 / 1.2;

    this.zoomScale = Math.max(
      1,
      Math.min(5, this.zoomScale * factor)
    );

    this.clampZoomPosition();
    this.applyZoom();
  };

  handleZoomClick = (event) => {
    if (event.detail > 1) return;

    if (this.suppressZoomClick) {
      this.suppressZoomClick = false;
      return;
    }

    const result = this.getZoomImageFromEvent(event);

    if (!result) return;

    const { image, container } = result;

    if (
      this.zoomMedia &&
      this.zoomMedia !== container
    ) {
      this.resetZoom();
    }

    this.zoomImage = image;
    this.zoomMedia = container;

    if (this.zoomScale === 1) {
      this.zoomScale = 2;
    } else {
      this.zoomScale = 1;
    }

    this.zoomX = 0;
    this.zoomY = 0;

    this.applyZoom();
  };

  handleZoomPointerDown = (event) => {
    if (
      event.pointerType !== 'mouse' ||
      event.button !== 0 ||
      this.zoomScale <= 1
    ) {
      return;
    }

    const result = this.getZoomImageFromEvent(event);

    if (
      !result ||
      result.container !== this.zoomMedia
    ) {
      return;
    }

    event.preventDefault();

    this.zoomDragging = true;
    this.zoomMoved = false;
    this.zoomPointerId = event.pointerId;

    this.lastZoomX = event.clientX;
    this.lastZoomY = event.clientY;

    event.target.setPointerCapture?.(
      event.pointerId
    );
  };

  handleZoomPointerMove = (event) => {
    if (
      !this.zoomDragging ||
      event.pointerId !== this.zoomPointerId
    ) {
      return;
    }

    const dx = event.clientX - this.lastZoomX;
    const dy = event.clientY - this.lastZoomY;

    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      this.zoomMoved = true;
    }

    this.zoomX += dx;
    this.zoomY += dy;

    this.lastZoomX = event.clientX;
    this.lastZoomY = event.clientY;

    this.clampZoomPosition();
    this.applyZoom();
  };

  handleZoomPointerUp = (event) => {
    if (
      event.pointerId !== this.zoomPointerId
    ) {
      return;
    }

    if (this.zoomMoved) {
      this.suppressZoomClick = true;
    }

    this.zoomDragging = false;
    this.zoomPointerId = null;

    const image = this.getZoomImage();

    if (image) {
      image.style.cursor = 'grab';
    }
  };

  handleZoomDoubleClick = (event) => {
    const result = this.getZoomImageFromEvent(event);

    if (!result) return;

    event.preventDefault();
    this.resetZoom();
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Finds the most visible gallery item.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    if (!elements.length) {
      resolve(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce(
          (prev, current) =>
            current.intersectionRatio >
            prev.intersectionRatio
              ? current
              : prev
        );

        observer.disconnect();
        resolve(mostVisible.target);
      },
      {
        threshold: Array.from(
          { length: 100 },
          (_, i) => i / 100
        ),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
